import { useState } from "react"

import {
  usePostAuthEmailVerify,
  usePostAuthEmailVerifyResend,
} from "@boundless/bl-api-client/allauth/auth"
import { useNavigate } from "@tanstack/react-router"
import mixpanel from "mixpanel-browser"

import { CodeEntryForm } from "@/components/account/CodeEntryForm"
import {
  type AllauthErrorType,
  getCodeRequestRateLimitedMessage,
  isCodeRequestRateLimitedResponse,
  isEmailVerificationOkError,
} from "@/lib/account/utils"
import { getAuthClient } from "@/lib/clients"
import { RESEND_SECONDS } from "@/lib/constants"
import { store } from "@/lib/store"
import { isToastableError } from "@/lib/utils"

export function VerifyEmailCodeForm({
  email,
  slug,
  onAuthenticated,
  onSignInRequired,
}: {
  email: string
  slug?: string
  // Override the post-verification destinations. The embed reuses this form
  // but routes within its own (`/embed/...`) tree instead of the app's.
  // The two callbacks map to the two ways verification can resolve:
  //   onAuthenticated  - code verified + a session was issued (logged in)
  //   onSignInRequired - code verified but no session; the user must sign in
  onAuthenticated?: () => void
  onSignInRequired?: () => void
}) {
  const client = getAuthClient()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  function goToLogin() {
    if (onSignInRequired) {
      onSignInRequired()
      return
    }
    if (slug) {
      navigate({
        to: "/p/$slug/login",
        params: { slug },
        search: { verified: true },
      })
      return
    }
    navigate({ to: "/login", search: { verified: true } })
  }

  const { isPending, mutate: verify } = usePostAuthEmailVerify({
    mutation: {
      onSuccess: (response) => {
        mixpanel.identify(response.data.user.id)
        store.setState({
          isAuthenticated: response.meta.is_authenticated,
          userRole: response.data.user.role,
        })
        if (onAuthenticated) {
          onAuthenticated()
          return
        }
        navigate({ to: "/home" })
      },
      onError: (verifyError) => {
        // allauth returns a 401 with only the login/signup flows when the
        // code verified the email but did not establish a session.
        if (isEmailVerificationOkError(verifyError)) {
          goToLogin()
          return
        }
        setError("That code is invalid or has expired. Please try again.")
      },
    },
  })

  const { mutate: resend } = usePostAuthEmailVerifyResend({
    mutation: {
      meta: {
        // The inline "please wait" message already covers the throttled case.
        shouldDisplayErrorToast: (error) =>
          isToastableError(error) &&
          !isCodeRequestRateLimitedResponse(error as AllauthErrorType),
      },
      onError: (resendError) => {
        if (isCodeRequestRateLimitedResponse(resendError)) {
          setError(getCodeRequestRateLimitedMessage(RESEND_SECONDS))
          return
        }
        setError("We couldn't resend the code right now. Please try again.")
      },
    },
  })

  return (
    <>
      <h2 className="text-4xl font-bold text-neutral-800">Verify your email</h2>
      <p className="my-4 text-lg text-neutral-500">
        Your account is ready - confirm your email to finish.
      </p>

      <CodeEntryForm
        email={email}
        isSubmitting={isPending}
        error={error}
        submitLabel="Verify & continue"
        onSubmit={(code) => {
          setError(null)
          verify({ client, data: { key: code } })
        }}
        onResend={() => {
          setError(null)
          resend({ client })
        }}
      />
    </>
  )
}
